import React, { useState, useEffect, useMemo } from "react";
import { ChevronUp, ChevronDown } from "lucide-react";
import { PortalDsButton } from "src/components/portal-design-system/portal-ds-button";
import { portalDs } from "src/components/portal-design-system/portal-ds-classes";
import { cn } from "src/lib/utils";
import {
  Sheet,
  SheetContent,
  SheetHeader,
  SheetTitle,
  SheetFooter,
} from "src/components/ui/sheet";
import { Input } from "src/components/ui/input";
import { CommissionTableEmptyState } from "./commission-table-empty-state";
import type { PayoutUser } from "../types/commissions.types";
import {
  useGetCommissionReportsQuery,
  useUpdateCommissionReportMutation,
  useGetCommissionRateOverridesQuery,
  useCreateCommissionRateOverrideMutation,
  useUpdateCommissionRateOverrideMutation,
  useDeleteCommissionRateOverrideMutation,
} from "../endpoints/commissions.endpoints";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "src/components/ui/table";
import { formatCurrency } from "../../utils/billing-utils";

function getMonthName(month: number): string {
  return new Date(2000, month - 1, 1).toLocaleString("default", { month: "long" });
}

interface PayoutUserDrawerProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  user: PayoutUser | null;
  isNew: boolean;
  onSave: (user: Partial<PayoutUser> & { id?: string }) => void;
}

const PayoutUserDrawer: React.FC<PayoutUserDrawerProps> = ({
  open,
  onOpenChange,
  user,
  isNew,
  onSave,
}) => {
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [activePercent, setActivePercent] = useState<string>("");
  const [breakdownExpanded, setBreakdownExpanded] = useState(false);
  /** Editable commission value per report row id (for month-by-month breakdown) */
  const [editingCommissionByReportId, setEditingCommissionByReportId] = useState<Record<string, string>>({});
  const [newOverrideYear, setNewOverrideYear] = useState(String(new Date().getFullYear()));
  const [newOverrideQuarter, setNewOverrideQuarter] = useState("1");
  const [newOverrideRate, setNewOverrideRate] = useState("");

  const { data: rateOverrides = [] } = useGetCommissionRateOverridesQuery(
    user?.id ? { userId: user.id } : undefined,
    { skip: !open || !user?.id || isNew }
  );
  const [createRateOverride] = useCreateCommissionRateOverrideMutation();
  const [updateRateOverride] = useUpdateCommissionRateOverrideMutation();
  const [deleteRateOverride] = useDeleteCommissionRateOverrideMutation();
  const [editingOverrideRateById, setEditingOverrideRateById] = useState<Record<string, string>>({});

  const { data: allUserReports = [] } = useGetCommissionReportsQuery(
    { type: "user" },
    { skip: !open || !user?.id || isNew }
  );
  const [updateCommissionReport] = useUpdateCommissionReportMutation();
  const userBreakdownRows = useMemo(() => {
    if (!user?.id) return [];
    const id = user.id;
    const filtered = allUserReports.filter(
      (r) => (r.userId ?? r.user) === id
    );
    return [...filtered].sort((a, b) => (a.year !== b.year ? a.year - b.year : a.month - b.month));
  }, [allUserReports, user?.id]);

  useEffect(() => {
    if (open) {
      setBreakdownExpanded(false);
      setEditingCommissionByReportId({});
      setEditingOverrideRateById({});
      setNewOverrideRate("");
      if (user) {
        setName(user.name);
        setEmail(user.email);
        setActivePercent(String(user.activePercent));
      } else if (isNew) {
        setName("");
        setEmail("");
        setActivePercent("");
      }
    }
  }, [open, user, isNew]);

  const handleSave = () => {
    const activeNum = parseFloat(activePercent);
    onSave({
      ...(user?.id ? { id: user.id } : {}),
      name: name.trim() || undefined,
      email: email.trim() || undefined,
      activePercent: Number.isNaN(activeNum) ? undefined : activeNum,
    });
    onOpenChange(false);
  };

  return (
    <Sheet
      open={open}
      onOpenChange={onOpenChange}
    >
      <SheetContent className="!w-1/2 !max-w-none overflow-y-auto bg-white">
        <SheetHeader>
          <SheetTitle className={portalDs.sheetTitle}>
            {isNew ? "Add Commission User" : "Edit Commission User"}
          </SheetTitle>
        </SheetHeader>
        {/* Name + Email row — same layout as referral drawer */}
        <div className="flex gap-4 py-4 items-end">
          <div className="flex-1 min-w-0 flex flex-col gap-1.5">
            <label className={portalDs.label}>Name</label>
            <Input
              value={name}
              onChange={(e) => setName(e.target.value)}
              placeholder="Full name"
              className={portalDs.input}
            />
          </div>
          <div className="flex-1 min-w-0 flex flex-col gap-1.5">
            <label className={portalDs.label}>Email</label>
            <Input
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              placeholder="email@company.com"
              className={portalDs.input}
            />
          </div>
        </div>
        {/* Rate (%) — above month-by-month breakdown */}
        <div className="flex gap-4 py-4 items-end">
          <div className="flex-1 min-w-0 max-w-xs flex flex-col gap-1.5">
            <label className={portalDs.label}>Rate (%)</label>
            <Input
              type="number"
              min={0}
              max={100}
              step={0.5}
              value={activePercent}
              onChange={(e) => setActivePercent(e.target.value)}
              placeholder="e.g. 10"
              className={portalDs.input}
            />
          </div>
        </div>
        {/* Per-quarter overrides */}
        {!isNew && user?.id && (
          <div className={cn(portalDs.formPanel, "mb-4 p-0 overflow-hidden")}>
            <div className="px-4 py-3 border-b border-[#EDEEF2]">
              <h3 className={portalDs.sheetSectionTitle}>Per-quarter overrides</h3>
              <p className={cn(portalDs.caption, "mt-0.5")}>
                Quarter-specific rate overrides replace the base rate for that quarter only.
              </p>
            </div>
            <div className="p-4 space-y-4">
              {rateOverrides.length > 0 ? (
                <div className={portalDs.tableContainer}>
                  <Table>
                    <TableHeader>
                      <TableRow className={portalDs.tableHead}>
                        <TableHead className={portalDs.tableHeadCell}>Quarter</TableHead>
                        <TableHead className={cn(portalDs.tableHeadCell, "text-right")}>Rate (%)</TableHead>
                        <TableHead className={cn(portalDs.tableHeadCell, "text-right w-24")}>Actions</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {rateOverrides.map((override) => {
                        const label = `${override.year}-Q${override.quarter}`;
                        const displayRate = editingOverrideRateById[override.id] ?? String(override.rate);
                        return (
                          <TableRow key={override.id} className={portalDs.tableBodyRow}>
                            <TableCell className={portalDs.tableCell}>{label}</TableCell>
                            <TableCell className={cn(portalDs.tableCell, "text-right")}>
                              <Input
                                type="number"
                                min={0}
                                step={0.01}
                                value={displayRate}
                                onChange={(e) =>
                                  setEditingOverrideRateById((prev) => ({ ...prev, [override.id]: e.target.value }))
                                }
                                onBlur={() => {
                                  const parsed = parseFloat(displayRate);
                                  if (!Number.isNaN(parsed) && parsed !== override.rate) {
                                    updateRateOverride({ id: override.id, rate: parsed }).unwrap().catch(() => {});
                                  }
                                  setEditingOverrideRateById((prev) => {
                                    const next = { ...prev };
                                    delete next[override.id];
                                    return next;
                                  });
                                }}
                                className={cn(portalDs.input, "h-8 w-24 text-right ml-auto")}
                              />
                            </TableCell>
                            <TableCell className={cn(portalDs.tableCell, "text-right")}>
                              <PortalDsButton
                                type="button"
                                variant="ghost"
                                size="sm"
                                onClick={() => deleteRateOverride({ id: override.id }).unwrap().catch(() => {})}
                              >
                                Remove
                              </PortalDsButton>
                            </TableCell>
                          </TableRow>
                        );
                      })}
                    </TableBody>
                  </Table>
                </div>
              ) : (
                <p className={portalDs.hint}>No per-quarter overrides configured.</p>
              )}
              <div className="flex flex-wrap items-end gap-3 pt-2 border-t border-[#EDEEF2]">
                <div className="flex flex-col gap-1.5">
                  <label className={portalDs.label}>Year</label>
                  <Input
                    type="number"
                    value={newOverrideYear}
                    onChange={(e) => setNewOverrideYear(e.target.value)}
                    className={cn(portalDs.input, "h-8 w-24")}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className={portalDs.label}>Quarter</label>
                  <Input
                    type="number"
                    min={1}
                    max={4}
                    value={newOverrideQuarter}
                    onChange={(e) => setNewOverrideQuarter(e.target.value)}
                    className={cn(portalDs.input, "h-8 w-20")}
                  />
                </div>
                <div className="flex flex-col gap-1.5">
                  <label className={portalDs.label}>Rate (%)</label>
                  <Input
                    type="number"
                    min={0}
                    step={0.01}
                    value={newOverrideRate}
                    onChange={(e) => setNewOverrideRate(e.target.value)}
                    placeholder="e.g. 0.85"
                    className={cn(portalDs.input, "h-8 w-28")}
                  />
                </div>
                <PortalDsButton
                  type="button"
                  size="sm"
                  variant="secondary"
                  onClick={() => {
                    const rate = parseFloat(newOverrideRate);
                    const year = parseInt(newOverrideYear, 10);
                    const quarter = parseInt(newOverrideQuarter, 10);
                    if (!user?.id || Number.isNaN(rate) || Number.isNaN(year) || Number.isNaN(quarter)) return;
                    createRateOverride({ userId: user.id, year, quarter, rate })
                      .unwrap()
                      .then(() => setNewOverrideRate(""))
                      .catch(() => {});
                  }}
                >
                  Add override
                </PortalDsButton>
              </div>
            </div>
          </div>
        )}
        {/* Month-by-month breakdown — same styling as referral drawer */}
        {!isNew && user?.id && (
          <div className={cn(portalDs.formPanel, "mb-4 p-0 overflow-hidden")}>
            <PortalDsButton
              type="button"
              variant="secondary"
              size="sm"
              className="flex items-center gap-2 w-full rounded-md justify-start"
              onClick={() => setBreakdownExpanded((p) => !p)}
            >
              {breakdownExpanded ? (
                <ChevronUp className="h-4 w-4 shrink-0" />
              ) : (
                <ChevronDown className="h-4 w-4 shrink-0" />
              )}
              <span>Month-by-month commission breakdown</span>
              {userBreakdownRows.length > 0 && (
                <span className={cn(portalDs.tableCellMuted, "font-normal")}>
                  ({userBreakdownRows.length} month{userBreakdownRows.length !== 1 ? "s" : ""})
                </span>
              )}
            </PortalDsButton>
            {breakdownExpanded && (
              <div className="overflow-hidden border-t border-[#EDEEF2]">
                <div className={portalDs.tableContainer}>
                  <Table>
                    <TableHeader>
                      <TableRow className={portalDs.tableHead}>
                        <TableHead className={portalDs.tableHeadCell}>Month</TableHead>
                        <TableHead className={portalDs.tableHeadCell}>Year</TableHead>
                        <TableHead className={cn(portalDs.tableHeadCell, "text-right w-1/4 min-w-[25%]")}>Commission</TableHead>
                      </TableRow>
                    </TableHeader>
                    <TableBody>
                      {userBreakdownRows.length === 0 ? (
                        <CommissionTableEmptyState
                          colSpan={3}
                          title="No commission data yet"
                          description="Monthly commission rows will appear here once reports are available for this user."
                        />
                      ) : (
                        userBreakdownRows.map((row) => {
                          const rowKey = row.id ?? `report-${row.year}-${row.month}`;
                          const displayValue = editingCommissionByReportId[rowKey] ?? String(Number((row.commission ?? 0).toFixed(2)));
                          const handleCommissionBlur = () => {
                            if (!row.id) return;
                            const parsed = parseFloat(displayValue);
                            if (!Number.isNaN(parsed) && parsed !== (row.commission ?? 0)) {
                              updateCommissionReport({ id: row.id, commission: parsed }).unwrap().catch(() => {});
                            }
                            setEditingCommissionByReportId((prev) => {
                              const next = { ...prev };
                              delete next[rowKey];
                              return next;
                            });
                          };
                          return (
                            <TableRow key={rowKey} className={portalDs.tableBodyRow}>
                              <TableCell className={portalDs.tableCell}>
                                {getMonthName(row.month)}
                              </TableCell>
                              <TableCell className={portalDs.tableCell}>{row.year}</TableCell>
                              <TableCell className={cn(portalDs.tableCell, "text-right font-medium w-1/4 min-w-[25%]")}>
                                {row.id ? (
                                  <Input
                                    type="number"
                                    min={0}
                                    step={0.01}
                                    value={displayValue}
                                    onChange={(e) =>
                                      setEditingCommissionByReportId((prev) => ({ ...prev, [rowKey]: e.target.value }))
                                    }
                                    onBlur={handleCommissionBlur}
                                    className={cn(portalDs.input, "h-8 w-full min-w-[6rem] max-w-[10rem] text-right ml-auto")}
                                  />
                                ) : (
                                  formatCurrency(Number((row.commission ?? 0).toFixed(2)))
                                )}
                              </TableCell>
                            </TableRow>
                          );
                        })
                      )}
                    </TableBody>
                  </Table>
                </div>
              </div>
            )}
          </div>
        )}
        <SheetFooter>
          <PortalDsButton type="button" variant="primary" size="sm" className="px-6" onClick={handleSave}>
            Save
          </PortalDsButton>
        </SheetFooter>
      </SheetContent>
    </Sheet>
  );
};

export default PayoutUserDrawer;
